import { PeriodType } from './constants';
import { Period } from 'typed/utils';

const TIMEZONE_OFFSET = (date => date.getTimezoneOffset() * 60 * 1000)(new Date()); // calculate milliseconds

export function utcToLocalAlignment(date) {
    return date ? new Date(+date + TIMEZONE_OFFSET) : date;
}

export function localToUTCAlignment(date) {
    return date ? new Date(+date - TIMEZONE_OFFSET) : date;
}

class DateRange {
    constructor(periodType = PeriodType.MONTH, now = new Date(), invert = false, useLocalTime = false) {
        this.periodType = periodType;
        this._last = this._align(now, useLocalTime);
        this._previous = this._align(now);
        this._next = this._align(now);
        this._first = this._align(now);
        this._length = null;

        // TODO implement useLocalTime for fields other than 'last'

        switch (this.periodType) {
            case PeriodType.DAY:
                this._previous.setUTCDate(this._last.getUTCDate() - 1);
                this._next.setUTCDate(this._last.getUTCDate() + 1);
                this._first.setUTCMonth(this._last.getUTCMonth() - 1);
                this._length = (this._last - this.first) / (1000 * 60 * 60 * 24);

                if (invert) {
                    this._last.setUTCMonth(this._last.getUTCMonth() + 1);
                    this._previous.setUTCMonth(this._previous.getUTCMonth() + 1);
                    this._next.setUTCMonth(this._next.getUTCMonth() + 1);
                    this._first.setUTCMonth(this._first.getUTCMonth() + 1);
                }

                break;
            case PeriodType.WEEK:
                this._last.setUTCDate(this._last.getUTCDate());
                this._previous.setUTCDate(this._last.getUTCDate() - 7);
                this._next.setUTCDate(this._last.getUTCDate() + 7);
                this._first.setUTCDate(this._last.getUTCDate() - 7 * 20);
                this._length = 20;

                if (invert) {
                    this._last.setUTCDate(this._last.getUTCDate() + 7 * 20);
                    this._previous.setUTCDate(this._previous.getUTCDate() + 7 * 20);
                    this._next.setUTCDate(this._next.getUTCDate() + 7 * 20);
                    this._first.setUTCDate(this._first.getUTCDate() + 7 * 20);
                }

                break;

            case PeriodType.MONTH:
                this._last.setUTCDate(1);
                this._previous.setUTCDate(1);
                this._previous.setUTCMonth(this._last.getUTCMonth() - 1);
                this._next.setUTCDate(1);
                this._next.setUTCMonth(this._last.getUTCMonth() + 1);
                this._first.setUTCDate(1);
                this._first.setUTCFullYear(this._last.getUTCFullYear() - 1);
                this._length = 12;

                if (invert) {
                    this._last.setUTCFullYear(this._last.getUTCFullYear() + 1);
                    this._previous.setUTCFullYear(this._previous.getUTCFullYear() + 1);
                    this._next.setUTCFullYear(this._next.getUTCFullYear() + 1);
                    this._first.setUTCFullYear(this._first.getUTCFullYear() + 1);
                }
                break;

            case PeriodType.QUARTER:
                this._last.setUTCDate(1);
                this._previous.setUTCDate(1);
                this._previous.setUTCQuarter(this._last.getUTCQuarter() - 1);
                this._next.setUTCDate(1);
                this._next.setUTCQuarter(this._last.getUTCQuarter() + 1);
                this._first.setUTCDate(1);
                this._first.setUTCFullYear(this._last.getUTCFullYear() - 1);
                this._length = 4;

                if (invert) {
                    this._last.setUTCFullYear(this._last.getUTCFullYear() + 1);
                    this._previous.setUTCFullYear(this._previous.getUTCFullYear() + 1);
                    this._next.setUTCFullYear(this._next.getUTCFullYear() + 1);
                    this._first.setUTCFullYear(this._first.getUTCFullYear() + 1);
                }
                break;

            case PeriodType.YEAR:
                this._last.setUTCDate(1);
                this._last.setUTCMonth(0);
                this._previous.setUTCDate(1);
                this._previous.setUTCMonth(0);
                this._previous.setUTCFullYear(this._last.getUTCFullYear() - 1);
                this._next.setUTCDate(1);
                this._next.setUTCMonth(0);
                this._next.setUTCFullYear(this._last.getUTCFullYear() + 1);
                this._first.setUTCDate(1);
                this._first.setUTCMonth(0);
                this._first.setUTCFullYear(this._last.getUTCFullYear() - 6);
                this._length = 6;

                if (invert) {
                    this._last.setUTCFullYear(this._last.getUTCFullYear() + 6);
                    this._previous.setUTCFullYear(this._previous.getUTCFullYear() + 6);
                    this._next.setUTCFullYear(this._next.getUTCFullYear() + 6);
                    this._first.setUTCFullYear(this._first.getUTCFullYear() + 6);
                }
                break;
            default:
                throw new Error(`Unknown PeriodType: ${periodType}.`);
        }


    }

    _align(date, useLocalTime = false) {
        date = date ? new Date(date) : new Date();

        if (!useLocalTime) {
            date.setUTCHours(0, 0, 0, 0);
        }
        else {
            date.setHours(0, 0, 0, 0);
        }

        switch (this.periodType) {
            case PeriodType.DAY:
                break;
            case PeriodType.WEEK:
                if (!useLocalTime) {
                    date.setUTCDate(date.getUTCDate() - (date.getUTCDay() === 0 ? 7 : date.getUTCDay()) + 1);
                }
                else {
                    date.setDate(date.getDate() - (date.getDay() === 0 ? 7 : date.getDay()) + 1);
                }
                break;
            case PeriodType.MONTH:
                if (!useLocalTime) {
                    date.setUTCDate(1);
                }
                else {
                    date.setDate(1);
                }
                break;
            case PeriodType.QUARTER:
                if (!useLocalTime) {
                    date.setUTCDate(1);
                    date.setUTCQuarter(date.getUTCQuarter());
                }
                else {
                    date.setDate(1);
                    date.setQuarter(date.getQuarter());
                }
                break;
            case PeriodType.YEAR:
                if (!useLocalTime) {
                    date.setUTCDate(1);
                    date.setUTCMonth(0);
                }
                else {
                    date.setDate(1);
                    date.setMonth(0);
                }
                break;
            default:
                throw new Error(`Unknown PeriodType: ${this.periodType}.`);
        }

        return date;
    }

    get first() {
        return this._first;
    }

    set first(value) {
        this._first = value;
        this._length = null;
    }

    get previous() {
        return this._previous;
    }

    get last() {
        return this._last;
    }

    set last(value) {
        this._last = value;
        this._length = null;
    }

    get next() {
        return this._next;
    }

    get length() {
        return this._length !== null ? this._length : this.ticks().length;
    }

    move(periodCount) {
        switch (this.periodType) {
            case PeriodType.DAY:
                this._first.setUTCDate(this._first.getUTCDate() + periodCount);
                this._previous.setUTCDate(this._previous.getUTCDate() + periodCount);
                this._next.setUTCDate(this._next.getUTCDate() + periodCount);
                this._last.setUTCDate(this._last.getUTCDate() + periodCount);
                if (this._length !== null) {
                    this._length = (this._last - this.first) / (1000 * 60 * 60 * 24);
                }

                break;
            case PeriodType.WEEK:
                this._first.setUTCDate(this._first.getUTCDate() + periodCount * 7);
                this._previous.setUTCDate(this._previous.getUTCDate() + periodCount * 7);
                this._next.setUTCDate(this._next.getUTCDate() + periodCount * 7);
                this._last.setUTCDate(this._last.getUTCDate() + periodCount * 7);
                break;
            case PeriodType.MONTH:
                this._first.setUTCMonth(this._first.getUTCMonth() + periodCount);
                this._previous.setUTCMonth(this._previous.getUTCMonth() + periodCount);
                this._next.setUTCMonth(this._next.getUTCMonth() + periodCount);
                this._last.setUTCMonth(this._last.getUTCMonth() + periodCount);
                break;
            case PeriodType.QUARTER:
                this._first.setUTCQuarter(this._first.getUTCQuarter() + periodCount);
                this._previous.setUTCQuarter(this._previous.getUTCQuarter() + periodCount);
                this._next.setUTCQuarter(this._next.getUTCQuarter() + periodCount);
                this._last.setUTCQuarter(this._last.getUTCQuarter() + periodCount);
                break;
            case PeriodType.YEAR:
                this._first.setUTCFullYear(this._first.getUTCFullYear() + periodCount);
                this._previous.setUTCFullYear(this._previous.getUTCFullYear() + periodCount);
                this._next.setUTCFullYear(this._next.getUTCFullYear() + periodCount);
                this._last.setUTCFullYear(this._last.getUTCFullYear() + periodCount);
                break;
            default:
                throw new Error(`Unknown PeriodType: ${this.periodType}.`);
        }

        return this;
    }

    collapse() {
        this._first = this._last;

        return this;
    }

    ticks(min, max) {
        min = this._align(min || this._first);
        max = this._align(max || this._last);
        if (min >= max) {
            return [max];
        }

        const result = [];

        switch (this.periodType) {
            case PeriodType.DAY:
                while (max >= min) {
                    result.push(new Date(max));
                    max.setUTCDate(max.getUTCDate() - 1);
                }
                break;
            case PeriodType.WEEK:
                while (max >= min) {
                    result.push(new Date(max));
                    max.setUTCDate(max.getUTCDate() - 7);
                }
                break;
            case PeriodType.MONTH:
                while (max >= min) {
                    result.push(new Date(max));
                    max.setUTCMonth(max.getUTCMonth() - 1);
                }
                break;
            case PeriodType.QUARTER:
                while (max >= min) {
                    result.push(new Date(max));
                    max.setUTCMonth(max.getUTCMonth() - 3);
                }
                break;
            case PeriodType.YEAR:
                while (max >= min) {
                    result.push(new Date(max));
                    max.setUTCFullYear(max.getUTCFullYear() - 1);
                }
                break;
            default:
                throw new Error(`Unknown PeriodType: ${this.periodType}.`);
        }

        return result.reverse();
    }

    periods(min, max) {
        min = new Period(this.periodType, min || this._first);
        max = new Period(this.periodType, max || this._last);

        const result = [];
        do {
            result.push(min);
            min = min.add(1);
        }
        while (min <= max);

        return result;
    }
}

export default DateRange;

